import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import type React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import ExternalLink from '@/components/ui/external-link';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { ClientCategorisation } from '@/constants';
import { type RouteConfig, useRouteConfig } from '@/context/route-config';
import { isFuturesAllowedForCountryAndCategorisation } from '@/features/futures/utils/country-futures-eligibility';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import { useProfileClientCategorisationTreatment, useProfileClientCategorisationTreatmentQuestionnaireMutation } from '@/hooks/queries/use-profile-client-categorisation-treatment';
import { useUserAttributes } from '@/hooks/queries/use-user-attributes';
import { isInstitutionalUserSegment } from '@/utils/user-segment';

type FuturesClientCategorisationTreatmentProps = {
  clientCategorisation: ClientCategorisation;
  countryOfResidency: string;
  reprocessFuturesEligibility?: boolean;
};

export interface FuturesClientCategorisationTreatmentRouteConfig extends RouteConfig {
  appropriatenessTest: string;
  clientCategorisation?: string;
  jurisdictionUnavailable: string;
  professionalAgreement: string;
}

const FuturesClientCategorisationTreatmentRightsLink = () => {
  const { t } = useTranslation();

  return (
    <ExternalLink
      href="https://drive.google.com/file/d/156YuuOgpUjgdKnqkfzH5hn_McJgIdVHW/view?usp=sharing"
      className="inline-block text-xs text-muted-foreground underline hover:no-underline"
    >
      {t('FuturesAppropriatenessClientCategorisation.treatment.rights')}
    </ExternalLink>
  );
};
FuturesClientCategorisationTreatmentRightsLink.displayName = 'FuturesClientCategorisationTreatmentRightsLink';

const FuturesClientCategorisationTreatmentContentRetail = ({ handleRetailSelection, isPending }: { handleRetailSelection: VoidFunction; isPending: boolean }) => {
  const routeConfig = useRouteConfig<FuturesClientCategorisationTreatmentRouteConfig>();
  const navigate = useNavigate();
  const { data: userAttributes } = useUserAttributes();
  const { t } = useTranslation();
  const clientCategorisationRoute = routeConfig.clientCategorisation;
  const textKey = isInstitutionalUserSegment(userAttributes?.segment)
    ? 'FuturesAppropriatenessClientCategorisation.treatment.retail.institutionText'
    : 'FuturesAppropriatenessClientCategorisation.treatment.retail.text';

  return (
    <div className="relative">
      <PageHeading>
        <PageHeadingTitle>{t('FuturesAppropriatenessClientCategorisation.treatment.title')}</PageHeadingTitle>
      </PageHeading>
      {clientCategorisationRoute ? <PageHeadingBackButton onClick={() => navigate(clientCategorisationRoute, { replace: true })} /> : null}
      <PageCard size="lg" bordered fixedFooter>
        <PageCardContent className="space-y-4 pt-8">
          {(t(textKey, { returnObjects: true }) as string[]).map((text, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: index matches translation index
            <p key={index}>{text}</p>
          ))}
          <FuturesClientCategorisationTreatmentRightsLink />
        </PageCardContent>
        <PageCardFooter className="flex flex-wrap gap-4">
          <Button className="flex-1 relative" size="lg" type="submit" variant="primary" disabled={isPending} onClick={handleRetailSelection}>
            <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
            {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
          </Button>
        </PageCardFooter>
      </PageCard>
    </div>
  );
};
FuturesClientCategorisationTreatmentContentRetail.displayName = 'FuturesClientCategorisationTreatmentContentRetail';

const FuturesClientCategorisationTreatmentContentProfessional = ({
  countryOfResidency,
  handleRetailSelection,
  isPending,
}: FuturesClientCategorisationTreatmentProps & {
  handleRetailSelection: VoidFunction;
  isPending: boolean;
}) => {
  const { data: userAttributes } = useUserAttributes();
  const { data: countries } = useMainstreamCountries();
  const routeConfig = useRouteConfig<FuturesClientCategorisationTreatmentRouteConfig>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { segment: userSegment } = userAttributes ?? {};

  const handleProfessionalSelection = useCallback(() => {
    /*
     * The country's allowedProducts (from the countries response) gate access by client categorisation.
     * Institutional users are exempt from this restriction.
     */
    if (!isInstitutionalUserSegment(userSegment) && !isFuturesAllowedForCountryAndCategorisation(countries?.[countryOfResidency], ClientCategorisation.PROFESSIONAL)) {
      navigate(routeConfig.jurisdictionUnavailable, { replace: true });
      return;
    }

    navigate(routeConfig.professionalAgreement, { replace: true });
  }, [countries, countryOfResidency, navigate, routeConfig, userSegment]);

  return (
    <>
      <PageHeading>
        <PageHeadingTitle>{t('FuturesAppropriatenessClientCategorisation.treatment.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('FuturesAppropriatenessClientCategorisation.treatment.description')}</PageHeadingDescription>
        {/* The client categorisation page for institution users does not include this text (containing leverage information), so we add it here */}
        {isInstitutionalUserSegment(userSegment) ? (
          <PageHeadingDescription>{t('FuturesAppropriatenessClientCategorisation.treatment.descriptionProtections')}</PageHeadingDescription>
        ) : null}
      </PageHeading>
      <PageCard size="lg" style={{ '--page-card-fixed-footer-height': '9.5rem' } as React.CSSProperties} bordered fixedFooter>
        <PageCardContent className="space-y-4 pt-8">
          {(t('FuturesAppropriatenessClientCategorisation.treatment.professional.text', { returnObjects: true }) as string[]).map((text, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: index matches translation index
            <p key={index}>{text}</p>
          ))}
          <FuturesClientCategorisationTreatmentRightsLink />
        </PageCardContent>
        <PageCardFooter className="flex gap-4 -md:!flex-col -md:!items-stretch">
          <Button className="flex-1 relative" size="lg" type="submit" variant="outline" disabled={isPending} onClick={handleRetailSelection}>
            <span className={isPending ? 'invisible' : ''}>{t('FuturesAppropriatenessClientCategorisation.treatment.professional.actions.retail')}</span>
            {isPending ? <Loading position="overlay" size="sm" /> : null}
          </Button>
          <Button className="flex-1 relative" size="lg" type="submit" variant="primary" disabled={isPending} onClick={handleProfessionalSelection}>
            {t('FuturesAppropriatenessClientCategorisation.treatment.professional.actions.professional')}
          </Button>
        </PageCardFooter>
      </PageCard>
    </>
  );
};
FuturesClientCategorisationTreatmentContentProfessional.displayName = 'FuturesClientCategorisationTreatmentContentProfessional';

const FuturesClientCategorisationTreatmentContent = (props: FuturesClientCategorisationTreatmentProps) => {
  const { clientCategorisation, countryOfResidency, reprocessFuturesEligibility } = props;
  const { isPending, mutate } = useProfileClientCategorisationTreatmentQuestionnaireMutation();
  const { data: userAttributes } = useUserAttributes();
  const routeConfig = useRouteConfig<FuturesClientCategorisationTreatmentRouteConfig>();
  const { data: countries } = useMainstreamCountries();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { segment: userSegment } = userAttributes ?? {};

  const handleRetailSelection = useCallback(() => {
    /*
     * The country's allowedProducts (from the countries response) gate access by client categorisation.
     * Institutional users are exempt from this restriction.
     */
    if (!isInstitutionalUserSegment(userSegment) && !isFuturesAllowedForCountryAndCategorisation(countries?.[countryOfResidency], ClientCategorisation.RETAIL)) {
      navigate(routeConfig.jurisdictionUnavailable, { replace: true });
      return;
    }

    mutate(
      {
        answers: { treatment: ClientCategorisation.RETAIL },
        reprocessFuturesEligibility,
      },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => navigate(routeConfig.appropriatenessTest, { replace: true }),
      },
    );
  }, [countries, countryOfResidency, mutate, navigate, reprocessFuturesEligibility, routeConfig, t, toast, userSegment]);

  if (!countryOfResidency || !clientCategorisation) return null;

  return {
    [ClientCategorisation.RETAIL]: <FuturesClientCategorisationTreatmentContentRetail handleRetailSelection={handleRetailSelection} isPending={isPending} />,
    [ClientCategorisation.PROFESSIONAL]: <FuturesClientCategorisationTreatmentContentProfessional {...props} handleRetailSelection={handleRetailSelection} isPending={isPending} />,
  }[clientCategorisation];
};
FuturesClientCategorisationTreatmentContent.displayName = 'FuturesClientCategorisationTreatmentContent';

const FuturesClientCategorisationTreatment = (props: FuturesClientCategorisationTreatmentProps) => {
  const { isLoading: isLoadingClientCategorisationTreatment, data: clientCategorisationTreatment } = useProfileClientCategorisationTreatment();
  const { isLoading: isLoadingCountries } = useMainstreamCountries();
  const { isLoading: isLoadingUserAttributes } = useUserAttributes();
  const routeConfig = useRouteConfig<FuturesClientCategorisationTreatmentRouteConfig>();

  if (isLoadingClientCategorisationTreatment || isLoadingCountries || isLoadingUserAttributes) return <LoadingCard />;

  if (clientCategorisationTreatment) return <Navigate to={routeConfig.appropriatenessTest} replace />;

  return <FuturesClientCategorisationTreatmentContent {...props} />;
};
FuturesClientCategorisationTreatment.displayName = 'FuturesClientCategorisationTreatment';

export default FuturesClientCategorisationTreatment;
